import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic, Pause, Play, Square, ArrowLeft, Loader2, Sparkles, ScanLine, Check, Camera, ChevronRight,
} from "lucide-react";
import { describeError } from "@/lib/api";
import {
  ensureApiKey, ensureNpi, ensurePatient, quickContext, deepContext, engagementEvents,
  createSession, sendToClaim, listProviders, sameDaySession, continueSession,
  generateSoap, getSoap, soapFromDictation, codesFromDictation,
  saveSessionTranscript, getNote, codesFromNote,
  analyzeSegment, normalizeInsights,
  patchEngagement, represcribeAlerts, stageReprescribe,
} from "@/lib/dictation";
import "@/styles/dictate.css";
import { startLuminaVoice, micSupported } from "@/lib/luminaVoice";
import WoundCare from "@/components/dictate/WoundCare";

const SPECIALTIES = ["Internal Medicine", "Family Medicine", "Endocrinology", "Cardiology", "Pediatrics", "OB-GYN"];
const initials = (name = "") => name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "PT";
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const short = (s, n = 60) => (s && s.length > n ? s.slice(0, n) + "…" : s || "");
const flagTone = (sev = "") => {
  const s = String(sev).toLowerCase();
  if (/(crit|high|urgent|severe|danger|red)/.test(s)) return "st-red";
  if (/(warn|moderate|medium|attention|amber|elevated)/.test(s)) return "st-amber";
  return "st-neutral";
};

// Turn the raw upstream NPI-validation error into a plain-English message.
// (The note engine rejects placeholder/unregistered NPIs like 1234567890 with
// a "npi_number too short / invalid npi" 422.)
const NPI_RX = /npi[_ ]?number|invalid npi|string_too_short|at least 10 characters/i;
const friendlyNoteError = (raw = "") =>
  NPI_RX.test(String(raw))
    ? "This provider’s NPI isn’t valid or isn’t registered. Please pick a registered provider from the Rendering provider list, then generate the note again."
    : (raw || "The note could not be generated.");

export default function Dictate() {
  const [params] = useSearchParams();
  const ctx = useMemo(() => ({
    clientId: params.get("client_id") || "",
    patientId: params.get("patient_id") || params.get("chart_uuid") || "",
    chartId: params.get("chart_id") || "",
    mrn: params.get("mrn") || "",
    name: params.get("name") || "",
    dob: params.get("dob") || "",
    gender: params.get("gender") || "",
  }), [params]);

  const [view, setView] = useState("session"); // session | generating | note
  const [genStep, setGenStep] = useState(0); // 0 preparing · 1 drafting
  const [genElapsed, setGenElapsed] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");

  const [chart, setChart] = useState(null);       // patient-context/quick
  const [events, setEvents] = useState([]);        // engagement checklist
  const [providers, setProviders] = useState([]);
  const [npi, setNpi] = useState("");
  const [session, setSession] = useState(null);
  const [specialty, setSpecialty] = useState(SPECIALTIES[0]);
  const [dos, setDos] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [mode, setMode] = useState("dict");        // dict | amb
  const [ambientConsent, setAmbientConsent] = useState(false);

  // recorder — live mic via Lumina Voice WS (falls back to manual transcript)
  const [rec, setRec] = useState("idle"); // idle | live | paused
  const [secs, setSecs] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [rail, setRail] = useState("clin");
  const [checked, setChecked] = useState({});
  const [woundCount, setWoundCount] = useState(0);
  const [insights, setInsights] = useState(null);
  const [insightBusy, setInsightBusy] = useState(false);
  const [rxAlerts, setRxAlerts] = useState([]);
  const [rxStaged, setRxStaged] = useState({});
  const [chartLoading, setChartLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const analyzeRef = useRef({ t: null, last: "" });
  const tickRef = useRef(null);
  const voiceRef = useRef(null);

  // note: drafted via soap-note; claim created on demand via send-to-ehi-claim
  const [soap, setSoap] = useState(null);
  const [codes, setCodes] = useState({ icd: [], cpt: [], visitId: "" });
  const [claimStage, setClaimStage] = useState("");
  const [soapId, setSoapId] = useState("");
  const [noteId, setNoteId] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [soapErr, setSoapErr] = useState("");
  const [noteView, setNoteView] = useState("l");
  const [noteTab, setNoteTab] = useState("note");
  const pollRef = useRef(null);
  const bootRef = useRef(false);

  useEffect(() => { document.title = "Dictation · revQ"; }, []);

  // ---- provision on mount -------------------------------------------------
  // Render the workspace shell immediately, then fill each section in as its
  // API call resolves (chart, engagement checklist, providers, session all run
  // in parallel — no single slow call blocks the page).
  useEffect(() => {
    if (bootRef.current) return; bootRef.current = true;
    if (!ctx.patientId) { setError("No patient was passed to this dictation session."); setStatus("error"); return; }
    setStatus("ready");
    ensureApiKey().catch(() => {});

    // providers → default NPI / specialty (+ register the full provider record)
    listProviders().then((provs) => {
      setProviders(provs);
      const first = provs.find((p) => p.status === "active") || provs[0];
      if (first?.npi_number) {
        setNpi(first.npi_number);
        const tax = (first.taxonomies || []).find((t) => t.primary) || (first.taxonomies || [])[0];
        if (tax?.desc) { const m = SPECIALTIES.find((s) => tax.desc.includes(s.split(" ")[0])); if (m) setSpecialty(m); }
        ensureNpi({ npi_number: first.npi_number, provider_name: first.provider_name, taxonomy_code: tax?.code, taxonomy_description: tax?.desc }).catch(() => {});
      }
    }).catch(() => setProviders([]));

    // chart context: quick call first for instant grounding, then the deep
    // full-EHR call to enrich it (deep fields supersede quick when present).
    quickContext({ patient_id: ctx.patientId, chart_uuid: ctx.patientId, client_id: ctx.clientId })
      .then((c) => setChart(c || null)).catch(() => setChart(null))
      .finally(() => setChartLoading(false));
    deepContext({ patient_id: ctx.patientId, chart_uuid: ctx.patientId, client_id: ctx.clientId })
      .then((deep) => { if (deep && typeof deep === "object") setChart((prev) => ({ ...(prev || {}), ...deep })); })
      .catch(() => { /* deep grounding is best-effort; quick already set the chart */ });

    // care-engagement checklist (loads independently — page never waits on it)
    engagementEvents({ patient_key: ctx.patientId, chart_uuid: ctx.patientId, client_id: ctx.clientId, dob: ctx.dob || undefined, gender: ctx.gender || undefined })
      .then((ev) => {
        const list = Array.isArray(ev?.events) ? ev.events : [];
        setEvents(list);
        setChecked(Object.fromEntries(list.map((e) => [e.id, e.status === "done"])));
      }).catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));

    // re-prescribe alerts
    represcribeAlerts(ctx.patientId).then((r) => {
      const list = [...(r?.ending_soon || []), ...(r?.recently_ended || [])];
      setRxAlerts(list);
      setRxStaged(Object.fromEntries((r?.staged || []).map((m, i) => [m.id || m.rxnorm || m.name || i, true])));
    }).catch(() => {});

    // ensure the patient exists + open a session. #5: continue a same-day session
    // for this patient if one exists, otherwise create a fresh one.
    (async () => {
      try {
        await ensurePatient({ id: ctx.patientId, patient_id: ctx.patientId, name: ctx.name, dob: ctx.dob, gender: ctx.gender, mrn: ctx.mrn, client_id: ctx.clientId }).catch(() => {});
        let s = null;
        try {
          const existing = await sameDaySession({ patient_id: ctx.patientId, client_id: ctx.clientId });
          const found = existing?.session || existing;
          if (found?.id) {
            s = found;
            await continueSession(found.id).catch(() => {});
            if (found.transcript) setTranscript(found.transcript);
            if (found.duration_seconds) setSecs(found.duration_seconds);
          }
        } catch { /* no same-day session → create new */ }
        if (!s?.id) s = await createSession({ patient_id: ctx.patientId, client_id: ctx.clientId });
        setSession(s);
      } catch { /* generate will surface a session error if it never lands */ }
    })();
  }, [ctx]);

  useEffect(() => () => { clearInterval(tickRef.current); clearTimeout(pollRef.current); clearTimeout(analyzeRef.current.t); try { voiceRef.current?.stop(); } catch {} }, []);

  // ---- live insights: analyze the growing transcript (debounced) ----------
  useEffect(() => {
    if (status !== "ready" || view !== "session") return;
    const text = transcript.trim();
    if (text.length < 25 || text === analyzeRef.current.last) return;
    clearTimeout(analyzeRef.current.t);
    analyzeRef.current.t = setTimeout(async () => {
      analyzeRef.current.last = text;
      setInsightBusy(true);
      try {
        // #7: build on the prior analysis — send its summary as previous_context.
        const d = await analyzeSegment(text, analyzeRef.current.prev || "");
        const norm = normalizeInsights(d);
        if (norm?.summary) analyzeRef.current.prev = norm.summary;
        setInsights(norm);
      } catch { /* insights are best-effort */ }
      finally { setInsightBusy(false); }
    }, 1800);
    return () => clearTimeout(analyzeRef.current.t);
  }, [transcript, status, view, session, ctx.patientId]);

  // ---- live microphone (Lumina Voice real-time transcription) -------------
  const startRec = async () => {
    if (mode === "amb" && !ambientConsent) { toast.error("Ambient capture needs patient consent first."); return; }
    if (!micSupported()) {
      toast.error("Live mic isn’t supported here — type the encounter in the Transcript tab.");
      setRail("trans"); return;
    }
    try {
      const token = sessionStorage.getItem("revq.token");
      await ensureApiKey().catch(() => {}); // make sure a streaming credential exists
      setInterim("");
      voiceRef.current = await startLuminaVoice({
        token,
        onFinal: (t) => { setTranscript((prev) => (prev ? prev.replace(/\s*$/, "") + " " : "") + t); setInterim(""); },
        onInterim: (t) => setInterim(t),
        onError: (msg) => toast.error(msg),
      });
      setRec("live");
      setRail("trans");
      tickRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (e) {
      toast.error(e?.message || "Couldn’t start the microphone — you can type the encounter instead.");
      setRail("trans");
    }
  };
  const togglePause = () => {
    if (rec === "live") { clearInterval(tickRef.current); voiceRef.current?.pause(); setRec("paused"); }
    else { tickRef.current = setInterval(() => setSecs((s) => s + 1), 1000); voiceRef.current?.resume(); setRec("live"); }
  };

  const patientName = chart?.patient_name || ctx.name || "Patient";
  const allergies = Array.isArray(chart?.allergies) ? chart.allergies : [];
  const conditions = Array.isArray(chart?.conditions) ? chart.conditions.filter((c) => (c.status || "active") === "active") : [];
  const meds = Array.isArray(chart?.medications) ? chart.medications : [];
  const vitals = Array.isArray(chart?.vitals) ? chart.vitals : [];
  const abnormal = Array.isArray(chart?.abnormal_labs) ? chart.abnormal_labs : [];
  const urgent = (Array.isArray(chart?.alerts) ? chart.alerts : []).filter((a) => ["warning", "critical", "high"].includes((a.severity || "").toLowerCase()));

  // ---- generate the DRAFT note via the dictation soap-note endpoint -------
  // Two-step flow: draft here (no claim), then Send to Note-to-Claim on demand.
  const pollDraft = useCallback((id, tries = 0) => {
    pollRef.current = setTimeout(async () => {
      setGenElapsed(tries * 3);
      try {
        const d = await getSoap(id);
        const st = (d.status || "").toLowerCase();
        if (st === "completed") { setSoap(soapFromDictation(d.result)); setCodes(codesFromDictation(d.result)); setSoapErr(""); setView("note"); return; }
        if (st === "failed" || st === "error") { setSoapErr(friendlyNoteError((d.result && d.result.error) || d.error)); setSoap(null); setView("note"); return; }
        if (tries > 40) { setSoapErr("Timed out waiting for the note. Please try again."); setView("note"); return; }
        pollDraft(id, tries + 1);
      } catch (e) { setSoapErr(friendlyNoteError(describeError(e))); setView("note"); }
    }, 3000);
  }, []);

  const runGeneration = async (text) => {
    if (!npi) { toast.error("A rendering provider (NPI) is required to generate the note."); setRail("clin"); setView("session"); return; }
    setView("generating"); setGenStep(0); setGenElapsed(0); setSoap(null); setSoapErr(""); setCodes({ icd: [], cpt: [], visitId: "" }); setClaimStage("");
    setNoteId(""); setClaimed(false);
    try {
      // The page renders before the session lands — make sure one exists now.
      let sess = session;
      if (!sess?.id) {
        await ensurePatient({ id: ctx.patientId, patient_id: ctx.patientId, name: ctx.name, dob: ctx.dob, gender: ctx.gender, mrn: ctx.mrn, client_id: ctx.clientId }).catch(() => {});
        sess = await createSession({ patient_id: ctx.patientId, client_id: ctx.clientId });
        setSession(sess);
      }
      if (!sess?.id) { setSoapErr("No active session."); setView("note"); return; }
      await ensureApiKey().catch(() => {});
      if (npi) await ensureNpi({ npi_number: npi }).catch(() => {});
      setGenStep(1);
      const r = await generateSoap({
        session_id: sess.id, dictation_session_id: sess.id,
        patient_id: ctx.patientId, client_id: ctx.clientId,
        npi_number: npi || undefined, specialty, transcript: text,
      });
      const id = r.soap_note_id || r.soap_id || r.id;
      if (!id) { setSoapErr(friendlyNoteError(r?.detail || "The note engine did not start.")); setView("note"); return; }
      setSoapId(id);
      pollDraft(id);
    } catch (e) { setSoapErr(friendlyNoteError(describeError(e))); setView("note"); }
  };

  const stopAndGenerate = async () => {
    clearInterval(tickRef.current); setRec("idle"); setInterim("");
    try { await voiceRef.current?.stop(); } catch {}
    voiceRef.current = null;
    await new Promise((r) => setTimeout(r, 150)); // let the final transcript land
    const text = transcript.trim();
    if (!text) { toast.error("Nothing was captured — dictate or type the encounter first."); setRail("trans"); return; }
    runGeneration(text);
  };

  const regenerate = () => {
    const text = transcript.trim();
    if (!text) { setView("session"); setRail("trans"); return; }
    runGeneration(text);
  };

  // ---- hand the reviewed note to Note-to-Claim (creates the claim visit) --
  // DQ-12: poll the documented stages with defer_derived=1 (cheap progress reads).
  // queued → instructions → communication → extracting → icd_validate → combinations → done.
  // Accepted in <1s; ready in ~55-70s for short dictations — allow up to ~5 min before giving up.
  const pollClaimCodes = useCallback((id, tries = 0) => {
    setTimeout(async () => {
      try {
        const n = await getNote(id, { defer_derived: 1 });
        const st = (n.status || "").toLowerCase();
        const stage = n.stage || "";
        setClaimStage(n.stage_message || stage || "");
        if (st === "error" || n.error) { setClaimStage(n.stage_message || "Claim build failed"); return; }
        if (st === "ready" || st === "completed" || stage === "done") { setCodes(codesFromNote(n)); setClaimStage(""); return; }
        if (tries > 100) { setClaimStage("Still working — check Create claims"); return; } // ~5 min
        pollClaimCodes(id, tries + 1);
      } catch { /* codes are a best-effort enrichment */ }
    }, 3000);
  }, []);

  const sendToClaimNow = async () => {
    if (!session?.id) return;
    if (!npi) { toast.error("A rendering provider (NPI) is required to send this to a claim."); return; }
    setClaiming(true);
    try {
      // #10: don't silently swallow a failed transcript save — the claim must not
      // be built from a session whose transcript was never persisted.
      try {
        await saveSessionTranscript(session.id, transcript.trim());
      } catch (e) {
        toast.error(`Couldn't save the transcript — not sending to claim. ${describeError(e)}`);
        return;
      }
      // #11: the engine expects the provider's taxonomy_code, not the specialty label.
      const prov = providers.find((p) => p.npi_number === npi);
      const primaryTax = (prov?.taxonomies || []).find((t) => t.primary) || (prov?.taxonomies || [])[0];
      // #3: map the chart insurance shape (carrier_name…) to the claim payer shape.
      const mapPolicy = (i) => i && ({
        payer_name: i.payer_name || i.carrier_name,
        payer_id: i.payer_id || i.carrier_id || undefined,
        member_id: i.member_id || i.policy_number || i.subscriber_id || undefined,
      });
      const r = await sendToClaim(session.id, {
        npi_number: npi,
        client_id: ctx.clientId || undefined,
        date_of_service: dos || undefined,
        taxonomy_code: primaryTax?.code || undefined,
        patient_name: patientName, patient_dob: chart?.dob || ctx.dob,
        patient_gender: chart?.gender || ctx.gender, patient_mrn: ctx.mrn,
        patient_insurance: mapPolicy(chart?.insurance?.[0]) || undefined,
        patient_insurance_secondary: mapPolicy(chart?.insurance_secondary || chart?.insurance?.[1]) || undefined,
        // DQ-03: stamp the new visit with its chart so the claim↔chart link is durable.
        chart_uuid: chart?.chart_uuid || ctx.patientId || undefined,
        chart_id: chart?.chart_id || undefined,
      });
      if (!r?.note_id) { toast.error(r?.detail || "The claim engine did not return a note."); return; }
      setNoteId(r.note_id); setClaimed(true);
      toast.success("Sent to Note-to-Claim — building the claim & codes");
      setNoteTab("bill");
      pollClaimCodes(r.note_id);
    } catch (e) { toast.error(describeError(e)); }
    finally { setClaiming(false); }
  };

  const openInClaims = () => {
    if (!noteId) return;
    window.open(`/create-claims?open=${encodeURIComponent(noteId)}`, "_blank");
  };

  const toggleCareItem = async (eventId, next) => {
    setChecked((c) => ({ ...c, [eventId]: next })); // optimistic
    try { await patchEngagement(eventId, ctx.patientId, next ? "done" : "pending"); }
    catch { setChecked((c) => ({ ...c, [eventId]: !next })); toast.error("Couldn't update that care item."); }
  };

  const stageRx = async (med) => {
    const key = med.id || med.rxnorm || med.name;
    setRxStaged((s) => ({ ...s, [key]: true })); // optimistic
    try { await stageReprescribe(ctx.patientId, med); toast.success(`${med.name || "Medication"} staged for renewal`); }
    catch { setRxStaged((s) => ({ ...s, [key]: false })); toast.error("Couldn't stage that renewal."); }
  };

  // ---- render -------------------------------------------------------------
  const crumbTail = view === "note" ? "Note" : view === "generating" ? "Generating" : "Session";

  return (
    <div className="lum" data-testid="dictate-page">
      <div className="app">
        <aside className="side">
          <div className="logo">rev<b>Q</b></div>
          <div className="navlab">Work</div>
          <button className="nav on"><Mic className="ic" size={16} /> Dictation</button>
          <div className="foot">Lumina Voice · in-app workspace<br />Patient opened from search</div>
        </aside>

        <div className="main">
          <div className="topbar">
            <div className="crumb"><span>Patients</span><span className="sep">/</span><b data-testid="dictate-crumb">{crumbTail}</b></div>
            <div className="topbar-right">
              <div className="flowsteps">
                <span className={view === "session" ? "active" : ""}>1 Session</span> →{" "}
                <span className={view === "generating" ? "active" : ""}>2 Generate</span> →{" "}
                <span className={view === "note" ? "active" : ""}>3 Note</span>
              </div>
              <button className="closebtn" onClick={() => window.close()} data-testid="dictate-close"><ArrowLeft size={13} /> Close</button>
            </div>
          </div>

          <div className="frame">
            {status === "loading" && (
              <div className="provwrap" data-testid="dictate-loading">
                <div><Loader2 className="lumspin" size={40} style={{ margin: "0 auto" }} />
                  <div style={{ marginTop: 14, fontWeight: 700, fontFamily: "var(--disp)", color: "var(--fg)" }}>Opening the dictation session</div>
                  <div style={{ marginTop: 4, fontSize: ".85rem" }}>Binding {ctx.name || "the patient"} and loading their chart…</div>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="card" data-testid="dictate-error"><div className="errbox">
                <div className="h">We couldn’t open the session</div>
                <div style={{ fontSize: ".85rem" }}>{error}</div>
                <div style={{ marginTop: 14 }}><button className="btn" onClick={() => window.location.reload()}>Try again</button></div>
              </div></div>
            )}

            {status === "ready" && view === "session" && (
              <SessionView {...{
                ctx, chart, patientName, allergies, conditions, meds, vitals, abnormal, urgent, events,
                providers, npi, setNpi, specialty, setSpecialty, dos, setDos, mode, setMode,
                ambientConsent, setAmbientConsent, rec, secs, startRec, togglePause, stopAndGenerate,
                transcript, setTranscript, interim, rail, setRail, checked, setChecked,
                insights, insightBusy, rxAlerts, rxStaged, toggleCareItem, stageRx,
                chartLoading, eventsLoading,
                sessionId: session?.id, woundCount, setWoundCount,
              }} />
            )}

            {status === "ready" && view === "generating" && (
              <>
                <div className="lumstrip"><span className="lmark">Lumina</span><span className="sep" /><span className="who">Drafting the note</span></div>
                <div className="card"><div className="generating" data-testid="dictate-generating">
                  <Loader2 className="lumspin" size={52} />
                  <div style={{ fontFamily: "var(--disp)", fontWeight: 800, color: "var(--fg)" }}>Lumina is drafting the note</div>
                  <div style={{ fontSize: ".84rem" }}>Drafting the SOAP note from the transcript and chart. Nothing is signed or billed yet.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18, width: "100%", maxWidth: 360 }} data-testid="dictate-gen-progress">
                    {[
                      { label: "Preparing session & provider", done: genStep > 0, active: genStep === 0 },
                      { label: "Drafting the SOAP note", done: false, active: genStep === 1 },
                    ].map((s, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".82rem", opacity: s.active || s.done ? 1 : 0.5 }}>
                        {s.done ? <Check size={16} strokeWidth={2.4} style={{ color: "var(--fg)" }} />
                          : s.active ? <Loader2 size={16} className="lumspin" />
                          : <span style={{ width: 16, height: 16, borderRadius: 999, border: "2px solid var(--line, #d8d8dc)", display: "inline-block" }} />}
                        <span style={{ fontWeight: s.active ? 700 : 500 }}>{s.label}</span>
                      </div>
                    ))}
                    {genStep === 1 && <div style={{ fontSize: ".72rem", opacity: 0.6, marginTop: 2 }}>Working… {genElapsed}s elapsed · this usually takes 10–30s</div>}
                  </div>
                </div></div>
              </>
            )}

            {status === "ready" && view === "note" && (
              <NoteView {...{ patientName, dos, npi, providers, soap, codes, claimStage, noteId, claimed, claiming, soapErr, noteView, setNoteView, noteTab, setNoteTab, transcript, regenerate, sendToClaimNow, openInClaims, backToSession: () => setView("session") }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Session view ---------------- */
function SessionView(props) {
  const {
    chart, patientName, allergies, conditions, meds, vitals, abnormal, urgent, events,
    providers, npi, setNpi, specialty, setSpecialty, dos, setDos, mode, setMode,
    ambientConsent, setAmbientConsent, rec, secs, startRec, togglePause, stopAndGenerate,
    transcript, setTranscript, interim, rail, setRail, checked, setChecked,
    insights, insightBusy, rxAlerts, rxStaged, toggleCareItem, stageRx,
    chartLoading, eventsLoading,
    sessionId, woundCount, setWoundCount,
  } = props;

  const critAllergy = allergies.find((a) => /anaphyl|severe|critical/i.test(JSON.stringify(a)));
  const prep = [
    ...abnormal.slice(0, 3).map((l) => ({ k: l.name, v: l.value + (l.unit ? ` ${l.unit}` : ""), t: l.date, flag: true })),
    ...vitals.slice(0, 2).map((v) => ({ k: v.name, v: v.value + (v.unit ? ` ${v.unit}` : ""), t: v.date, flag: false })),
  ].slice(0, 4);

  return (
    <>
      <div className="lumstrip">
        <span className="lmark">Lumina</span><span className="sep" />
        <span className="who">Clinical session · part of <b>AriaOne</b>, running in revQ</span>
        <span style={{ marginLeft: "auto" }} className="funnel">Dictate live — your words stream into the note as you speak</span>
      </div>

      <div className="card">
        <div className="idrow">
          <div className="avatar">{initials(patientName)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--disp)", fontWeight: 800 }} data-testid="dictate-patient-name">{patientName}</div>
            <div style={{ fontSize: ".8rem", color: "var(--ink-55)" }}>
              {chart?.age ? `${chart.age}y · ` : ""}{chart?.gender || ""}{chart?.insurance?.[0]?.carrier_name ? ` · ${chart.insurance[0].carrier_name}` : ""}
            </div>
          </div>
          {critAllergy
            ? <span className="status st-red">{critAllergy.name || critAllergy.allergen || "Critical allergy"}</span>
            : <span className="status st-neutral">{chartLoading ? "Checking allergies…" : allergies.length ? `${allergies.length} allergy` : "No known allergies"}</span>}
        </div>

        <div className="setup">
          <div className="grid">
            <div>
              <label>Rendering provider</label>
              <select value={npi} onChange={(e) => setNpi(e.target.value)} data-testid="dictate-provider">
                {providers.length === 0 && <option value="">No linked provider</option>}
                {providers.map((p) => <option key={p.npi_number} value={p.npi_number}>{p.provider_name} · NPI {p.npi_number}</option>)}
              </select>
              <div className="hint">Signs the note and carries the claim.</div>
            </div>
            <div>
              <label>Date of service</label>
              <input type="date" value={dos} onChange={(e) => setDos(e.target.value)} data-testid="dictate-dos" />
              <div className="hint">Defaults to today.</div>
            </div>
            <div>
              <label>Specialty</label>
              <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} data-testid="dictate-specialty">
                {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <div className="hint">Tunes clinical signals and vocabulary.</div>
            </div>
          </div>
          <div className="moderow">
            <div>
              <label>Mode</label>
              <div className="modes">
                <button className={`mode ${mode === "dict" ? "on" : ""}`} onClick={() => setMode("dict")} data-testid="dictate-mode-dictation"><Mic className="ic" size={15} /> Dictation</button>
                <button className={`mode ${mode === "amb" ? "on" : ""}`} onClick={() => setMode("amb")} data-testid="dictate-mode-ambient"><Sparkles className="ic" size={15} /> Ambient visit</button>
              </div>
            </div>
            <div className="hint" style={{ paddingBottom: 9 }}>{mode === "dict" ? "Your voice only — no patient audio, no consent needed." : "Captures the conversation — patient consent required before audio."}</div>
            <span style={{ marginLeft: "auto" }} />
            <button className="btn quiet" style={{ marginBottom: 4 }} onClick={() => toast("Scan a document into this encounter — coming soon")}><ScanLine className="ic" size={15} /> Scan with Lens</button>
          </div>
        </div>
      </div>

      <div className="sess">
        {/* LEFT — smart prep + clinical cards */}
        <div>
          <div className="ai" style={{ padding: "14px 16px" }}>
            <div className="ai-head"><span className="lmark">◆ Lumina</span> Visit prep
              <span style={{ color: "var(--ink-40)", fontWeight: 600, marginLeft: "auto", fontSize: ".74rem" }}>{chart?.source_notes ? `${chart.source_notes} source notes` : "chart-grounded"}</span></div>
            <div style={{ fontSize: ".88rem", marginTop: 7, lineHeight: 1.55 }}>
              {chartLoading ? "Loading the patient’s chart…" : conditions[0] ? `Focus areas from the chart: ${conditions.slice(0, 3).map((c) => c.name).join("; ")}.` : "No active problems on the chart yet."}
            </div>
            {prep.length > 0 && (
              <div className="prepgrid">
                {prep.map((p, i) => (
                  <div className={`prepcell ${p.flag ? "flag" : ""}`} key={i}>
                    <div className="k">{short(p.k, 22)}</div><div className="v">{p.v}</div>{p.t && <div className="t">{p.t}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="why"><b>Why now:</b> pulled live from the patient’s chart. <span className="honest">Nothing here is from audio — it exists before you speak.</span></div>
          </div>

          <div className="cardgrid">
            <div className="card">
              <div className="card-h"><h3>Active problems</h3><div className="right"><span className="tag">{conditions.length}</span></div></div>
              {conditions.length === 0 && <div className="pad honest">{chartLoading ? "Loading the chart…" : "No active problems on the chart."}</div>}
              {conditions.slice(0, 6).map((c, i) => (
                <div className="minirow" key={i}><span className="grow">{c.name} {c.code && <span className="code">{c.code}</span>}</span></div>
              ))}
            </div>
            <div className="card">
              <div className="card-h"><h3>Medications</h3><div className="right"><span className="tag">{meds.length}</span></div></div>
              {meds.length === 0 && <div className="pad honest">{chartLoading ? "Loading the chart…" : "No medications on the chart."}</div>}
              {meds.slice(0, 6).map((m, i) => (
                <div className="minirow" key={i}><span className="grow">{m.name} {m.dose && <span style={{ color: "var(--ink-55)" }}>{m.dose}</span>}</span></div>
              ))}
            </div>
            <div className="card">
              <div className="card-h"><h3>Vitals</h3><div className="right"><span className="tag">{vitals[0]?.date || "—"}</span></div></div>
              {vitals.length === 0 ? <div className="pad honest">{chartLoading ? "Loading the chart…" : "No vitals on the chart."}</div> : (
                <div className="vitgrid">{vitals.slice(0, 6).map((v, i) => (
                  <div className="vit" key={i}><div className="k">{short(v.name, 10)}</div><div className="v">{v.value}</div></div>
                ))}</div>
              )}
            </div>
            <div className="card">
              <div className="card-h"><h3>Allergies</h3><div className="right">{critAllergy ? <span className="status st-red">critical</span> : <span className="tag">{allergies.length}</span>}</div></div>
              {allergies.length === 0 ? <div className="pad honest">{chartLoading ? "Loading the chart…" : "No known allergies on chart."}</div> :
                allergies.slice(0, 5).map((a, i) => <div className="minirow" key={i}><span className="grow">{a.name || a.allergen || String(a)}</span></div>)}
            </div>
          </div>

          {rxAlerts.length > 0 && (
            <div className="card" data-testid="dictate-represcribe">
              <div className="card-h"><h3><span className="lmark" style={{ fontFamily: "var(--disp)", fontWeight: 800 }}>◆</span> Re-prescribe</h3>
                <div className="right"><span className="status st-amber">{rxAlerts.length} expiring</span></div></div>
              {rxAlerts.map((m, i) => {
                const key = m.id || m.rxnorm || m.name || i;
                const staged = !!rxStaged[key];
                return (
                  <div className="minirow" key={key}>
                    <span className="grow">{m.name || m.drug || "Medication"}{m.dose ? <span style={{ color: "var(--ink-55)" }}> {m.dose}</span> : null}
                      {(m.ends_on || m.end_date || m.days_left != null) && <span className="tag" style={{ marginLeft: 6 }}>{m.ends_on || m.end_date || `${m.days_left}d left`}</span>}</span>
                    <button className="btn" style={{ padding: "4px 12px" }} disabled={staged} onClick={() => stageRx(m)} data-testid={`represcribe-stage-${key}`}>
                      {staged ? "Staged ✓" : "Renew"}
                    </button>
                  </div>
                );
              })}
              <div className="vocab">Staged renewals ride along to the claim's e-Rx queue — nothing is sent until the provider signs.</div>
            </div>
          )}

          {urgent.length > 0 && (
            <div className="card" style={{ borderColor: "var(--amber-line)", background: "var(--amber-wash)" }}>
              <div className="card-h" style={{ borderBottomColor: "var(--amber-line)" }}><h3 style={{ color: "var(--amber)" }}>Clinical items to note</h3><div className="right"><span className="status st-amber">{urgent.length}</span></div></div>
              {urgent.slice(0, 4).map((a, i) => <div className="minirow" key={i} style={{ borderBottomColor: "var(--amber-line)" }}><span className="grow">{a.message}</span></div>)}
            </div>
          )}
        </div>

        {/* RIGHT — mic + rail */}
        <div>
          <button className="wc-trigger" onClick={() => setRail("wound")} data-testid="wound-quick-action-trigger">
            <span className="wc-trigger-ic"><Camera size={16} /></span>
            <span className="wc-trigger-txt">
              <span className="wc-trigger-l">Document a wound</span>
              <span className="wc-trigger-sub">Lumina Lens · photo → structured findings</span>
            </span>
            {woundCount > 0
              ? <span className="wc-trigger-badge" data-testid="wound-trigger-count">{woundCount} captured</span>
              : <ChevronRight size={16} className="wc-trigger-chev" />}
          </button>

          <div className="card sessioncard">
            <div className="pad" style={{ padding: "9px 14px", borderBottom: "1px solid var(--line-soft)", fontSize: ".78rem", color: "var(--ink-55)" }}>
              {mode === "dict" ? "Dictation — provider voice only" : "Ambient — captures the conversation"}
            </div>
            <div className="pad" style={{ paddingTop: 16 }}>
              <div className={`mic ${rec === "live" ? "live" : ""}`} data-testid="dictate-mic" onClick={() => rec === "idle" && startRec()}><Mic size={26} /></div>
              <div className="timer" data-testid="dictate-timer">{mmss(secs)}</div>
              <div className="state">{rec === "idle" ? "Ready — tap to dictate (your mic streams live)" : rec === "live" ? "Listening… speak now" : "Paused"}</div>
              {rec !== "idle" && interim && <div className="state" data-testid="dictate-interim" style={{ fontStyle: "italic", color: "var(--violet)", marginTop: 6 }}>“{interim}”</div>}
              {rec === "idle" ? (
                <div className="sessctl">
                  <button className="btn primary" onClick={startRec} data-testid="dictate-start">Start recording</button>
                  <button className="btn" onClick={stopAndGenerate} disabled={!transcript.trim()} data-testid="dictate-generate">Generate note →</button>
                </div>
              ) : (
                <div className="sessctl">
                  <button className="btn" onClick={togglePause} data-testid="dictate-pause">{rec === "live" ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Resume</>}</button>
                  <button className="btn stop" onClick={stopAndGenerate} data-testid="dictate-stop"><Square size={13} style={{ fill: "currentColor" }} /> Stop &amp; generate</button>
                </div>
              )}
            </div>
            <div className="pad" style={{ borderTop: "1px solid var(--line-soft)" }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                <div className="togglerow"><button className={`tg ${mode === "amb" ? "on" : ""}`} onClick={() => setMode(mode === "amb" ? "dict" : "amb")} /> Ambient</div>
                <div className="togglerow">Lang <span className="tag">EN</span></div>
              </div>
              {mode === "amb" && !ambientConsent && (
                <div className="consent" data-testid="dictate-consent">
                  <b>Ambient capture needs consent.</b> Recording the conversation requires the patient’s consent before audio starts.
                  <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap" }}>
                    <button className="btn" onClick={() => { setAmbientConsent(true); toast.success("Consent recorded for this visit"); }}>Consent obtained</button>
                    <button className="btn quiet" onClick={() => setMode("dict")}>Stay in dictation</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(insights || insightBusy) && (
            <div className="ai" style={{ padding: "12px 14px", marginBottom: 14 }} data-testid="dictate-insights">
              <div className="ai-head"><span className="lmark">◆ Lumina</span> Live insights
                {insightBusy && <Loader2 className="lumspin" size={13} style={{ marginLeft: "auto" }} />}</div>
              {insights?.summary && <div style={{ fontSize: ".82rem", marginTop: 7, lineHeight: 1.5, color: "var(--ink-70)" }}>{insights.summary}</div>}
              {insights?.flags?.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                  {insights.flags.map((f, i) => <span key={i} className={`status flagchip ${flagTone(f.severity)}`}>{f.message}</span>)}
                </div>
              )}
              {insights?.symptoms?.length > 0 && (
                <div style={{ marginTop: 9 }}>
                  <div style={{ fontSize: ".64rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: 5 }}>Heard this visit</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{insights.symptoms.map((s, i) => <span key={i} className="tag">{s}</span>)}</div>
                </div>
              )}
              {insights?.observations?.length > 0 && (
                <ul style={{ margin: "9px 0 0", paddingLeft: 16, fontSize: ".78rem", color: "var(--ink-55)", lineHeight: 1.5 }}>
                  {insights.observations.slice(0, 3).map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              )}
              {!insights && insightBusy && <div className="honest" style={{ marginTop: 6 }}>Listening for clinical cues…</div>}
              {insights && <div className="honest" style={{ marginTop: 8 }}>From what was said this visit — review before signing.</div>}
            </div>
          )}

          <div className="card">
            <div className="railtabs">
              <button className={`railtab ${rail === "clin" ? "on" : ""}`} onClick={() => setRail("clin")}>Clinical</button>
              <button className={`railtab ${rail === "trans" ? "on" : ""}`} onClick={() => setRail("trans")} data-testid="dictate-rail-transcript">Transcript</button>
              <button className={`railtab ${rail === "wound" ? "on" : ""}`} onClick={() => setRail("wound")} data-testid="dictate-rail-wounds">Wounds{woundCount > 0 && <span className="tag" style={{ marginLeft: 6 }}>{woundCount}</span>}</button>
              <button className={`railtab ${rail === "docs" ? "on" : ""}`} onClick={() => setRail("docs")}>Documents</button>
            </div>

            {rail === "clin" && (
              <div>
                <div className="card-h" style={{ borderBottom: "1px solid var(--line-soft)" }}><h3><span className="lmark" style={{ fontFamily: "var(--disp)", fontWeight: 800 }}>◆</span> Suggested for this visit</h3>
                  <div className="right"><span className="tag">{Object.values(checked).filter(Boolean).length}/{events.length} in note</span></div></div>
                <div className="suglist">
                  {events.length === 0 && <div className="pad honest">{eventsLoading ? "Loading care suggestions…" : "No care-engagement items for this patient."}</div>}
                  {events.map((e) => (
                    <label className="srow" key={e.id}>
                      <input type="checkbox" checked={!!checked[e.id]} onChange={(ev) => toggleCareItem(e.id, ev.target.checked)} />
                      <div className="grow"><div className="st">{e.title}</div><div className="sd">{e.detail}{e.reason ? ` · ${e.reason}` : ""}</div></div>
                    </label>
                  ))}
                </div>
                <div className="pad" style={{ paddingTop: 8, paddingBottom: 6 }}><div className="honest">From the chart, not the audio — these exist before you speak.</div></div>
              </div>
            )}

            {rail === "trans" && (
              <div className="pad">
                <label style={{ display: "block", fontSize: ".68rem", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: 6 }}>Encounter transcript {rec === "live" && <span style={{ color: "var(--red)", marginLeft: 6 }}>● live</span>}</label>
                <textarea className="transbox" data-testid="dictate-transcript" value={transcript + (interim ? (transcript ? " " : "") + interim : "")} onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Press Start recording and dictate — your speech transcribes here in real time. You can also type or edit directly; this text is what Lumina turns into the SOAP note." />
                <div className="honest" style={{ marginTop: 8 }}>Live transcription runs through Lumina Voice. Edit freely before generating.</div>
              </div>
            )}

            {rail === "docs" && (
              <div className="pad honest">Document scanning for this encounter is coming soon.</div>
            )}

            {rail === "wound" && (
              <WoundCare ctx={props.ctx} sessionId={sessionId} transcript={transcript} patientName={patientName} onCountChange={setWoundCount} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- Note view ---------------- */
function NoteView({ patientName, dos, npi, providers, soap, codes, claimStage, noteId, claimed, claiming, soapErr, noteView, setNoteView, noteTab, setNoteTab, transcript, regenerate, sendToClaimNow, openInClaims, backToSession }) {
  const provName = providers.find((p) => p.npi_number === npi)?.provider_name || "Provider";
  return (
    <>
      <div className="lumstrip">
        <span className="lmark">Lumina</span><span className="sep" />
        <span className="who"><b>Drafted by Lumina</b> · origin: dictation · reviews &amp; signs: {provName} — nothing reaches the chart or the claim unsigned</span>
        <span style={{ marginLeft: "auto" }} />
        {claimed && codes?.visitId && <span className="tag">{codes.visitId}</span>}
        {claimed && claimStage && <span className="tag" data-testid="dictate-claim-stage" style={{ opacity: 0.7 }}>{claimStage}</span>}
        <span className="tag">{provName} · DOS {dos}</span>
        <div className="viewseg"><button className={noteView === "l" ? "on" : ""} onClick={() => setNoteView("l")}>Lumina view</button><button className={noteView === "r" ? "on" : ""} onClick={() => setNoteView("r")}>revQ view</button></div>
      </div>

      <div className="card soap" data-testid="dictate-note">
        <div className="notetabs">
          <button className={`notetab ${noteTab === "note" ? "on" : ""}`} onClick={() => setNoteTab("note")}>Note</button>
          <button className={`notetab ${noteTab === "trans" ? "on" : ""}`} onClick={() => setNoteTab("trans")}>Transcript</button>
          <button className={`notetab ${noteTab === "bill" ? "on" : ""}`} onClick={() => setNoteTab("bill")}>Billing {(codes?.icd?.length || codes?.cpt?.length) ? <span className="tag" style={{ marginLeft: 4 }}>{(codes.icd?.length || 0) + (codes.cpt?.length || 0)}</span> : null}</button>
          <span style={{ marginLeft: "auto", alignSelf: "center", paddingRight: 4 }} className={`status ${claimed ? "st-ink" : "st-amber"}`}>{claimed ? "Sent to claim" : "Draft"}</span>
        </div>

        {soapErr && (
          <div className="errbox" data-testid="dictate-note-error">
            <div className="h">{/registered provider/.test(soapErr) ? "Pick a registered provider" : "The note couldn’t be generated"}</div>
            <div style={{ fontSize: ".85rem", maxWidth: 520, margin: "0 auto" }}>{soapErr}</div>
            <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "center" }}>
              {/registered provider/.test(soapErr)
                ? <button className="btn primary" onClick={backToSession} data-testid="dictate-pick-provider">Choose a provider</button>
                : <button className="btn primary" onClick={regenerate} data-testid="dictate-regenerate">Try again</button>}
              <button className="btn" onClick={backToSession}>Back to session</button>
            </div>
          </div>
        )}

        {!soapErr && noteTab === "note" && soap && (
          <div>
            {soap.chiefComplaint && <Section title="Chief complaint" text={soap.chiefComplaint} />}
            {soap.raw ? <Section title="Note" text={soap.raw} /> : (
              <>
                <Section title="Subjective" text={soap.subjective} />
                <Section title="Objective" text={soap.objective} />
                <Section title="Assessment" text={soap.assessment} />
                <Section title="Plan" text={soap.plan} />
              </>
            )}
          </div>
        )}

        {!soapErr && noteTab === "trans" && (
          <p style={{ whiteSpace: "pre-wrap" }}>{transcript || "No transcript."}</p>
        )}

        {!soapErr && noteTab === "bill" && (
          <div>
            {(codes?.icd?.length || codes?.cpt?.length) ? (
              <>
                {codes.icd?.length > 0 && <>
                  <div className="sh">Diagnoses · ICD-10</div>
                  {codes.icd.map((c, i) => (
                    <div className="minirow" key={i}><span className="code">{typeof c === "string" ? c : (c.code || "")}</span><span className="grow" style={{ marginLeft: 8 }}>{typeof c === "string" ? "" : (c.description || "")}</span></div>
                  ))}
                </>}
                {codes.cpt?.length > 0 && <>
                  <div className="sh">Procedures · CPT</div>
                  {codes.cpt.map((c, i) => (
                    <div className="minirow" key={i}><span className="code">{typeof c === "string" ? c : (c.code || "")}</span><span className="grow" style={{ marginLeft: 8 }}>{typeof c === "string" ? "" : (c.description || "")}</span>{c?.avg_paid?.amt != null && <span className="tag">~${c.avg_paid.amt}</span>}</div>
                  ))}
                </>}
              </>
            ) : <div className="pad honest">{claimed ? "Extracting billing codes…" : "Diagnosis previews appear here; final ICD/CPT are assigned when you send to Note-to-Claim."}</div>}
          </div>
        )}

        {!soapErr && (
          <div className="notebar">
            {claimed ? (
              <button className="btn primary" onClick={openInClaims} disabled={!noteId} data-testid="dictate-open-claim">Open in Note-to-Claim →</button>
            ) : (
              <button className="btn primary" onClick={sendToClaimNow} disabled={claiming} data-testid="dictate-send-claim">{claiming ? "Sending…" : "Send to Note-to-Claim →"}</button>
            )}
            <button className="btn" onClick={regenerate} data-testid="dictate-regenerate">Regenerate</button>
            <button className="btn quiet" onClick={backToSession}>Back to session</button>
          </div>
        )}
      </div>
    </>
  );
}

function Section({ title, text }) {
  return (
    <div className="soap">
      <div className="sh">{title}</div>
      <p>{text || "—"}</p>
    </div>
  );
}
